import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Groups } from '../../api/Groups';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { GroupModel } from '../../models/Group';
import { UserRole } from '../../models/User';

export default function GroupsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<GroupModel[] | null>();
  const [newGroup, setNewGroup] = useState<GroupModel>(new GroupModel());
  const [groupToDelete, setGroupToDelete] = useState<GroupModel>();
  const [editing, setEditing] = useState<number | undefined>();
  const [searchText, setSearchText] = useState<string>("");

  const [validated, setValidated] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();

  useEffect(() => {
    if (loggedUser) initGroups();
    // eslint-disable-next-line
  }, [loggedUser]);

  const openCreateModal = () => {
    setEditing(undefined);
    setShowEditModal(true);
    setNewGroup({} as GroupModel)
  }
  const handleDeleteModalClose = () => setShowDeleteModal(false);
  const handleCreateModalClose = () => {
    setShowEditModal(false);
    setValidated(false);
  }
  const openDeleteModal = (group: GroupModel) => () => {
    setShowDeleteModal(true);
    setGroupToDelete(group);
  }

  const reset = () => {
    setGroups(null);
    initGroups();
  }

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader!();

      const promise = editing !== undefined
        ? Groups.updateGroup(newGroup)
        : Groups.createGroup(newGroup);

      promise
        .then(
          () => {
            hideLoader!();
            showToast!('groups-success', `Group ${editing !== undefined ? 'Updated' : 'Created'} successfully`, 'Success', 'success');
            reset();
            setNewGroup(new GroupModel());
            handleCreateModalClose();
            if (editing !== undefined) setEditing(undefined);
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            hideLoader!();
            showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }

  }

  const handleGroupChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setNewGroup({
      ...newGroup,
      name: value,
    })
  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }

  const deleteGroup = () => {
    showLoader!();

    Groups.deleteGroup(groupToDelete!.id)
      .then(
        () => {
          hideLoader!();
          showToast!('group-success', 'Group deleted successfully', 'Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const editGroup = (index: number) => () => {
    setShowEditModal(true);
    setEditing(index);
    setNewGroup({ ...groups![index] });
  }

  const initGroups = () => {
    Groups.getGroups()
      .then(
        ({ data: groups }) => {
          if (loggedUser!.role === UserRole.DOCTOR) setGroups(groups.filter(group => loggedUser!.groups!.map((g) => g.id).includes(group.id!)));
          else setGroups(groups);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const manageGroup = (group: GroupModel) => () => navigate(`/groups/${group.id}/manage-members`);

  const renderGroups = () => {
    const filteredGroups = groups!.filter(group => group.name.toLowerCase().includes(searchText.toLowerCase()));

    return (
      <>
        <Row className="gx-2 mb-3">
          <Col>
            <AppSearch value={searchText} onChange={handleSearchChange} placeholder={t('groupsList.search')} />
          </Col>
        </Row>

        {filteredGroups.length === 0 && (
          <Alert className="mb-0 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('groupsList.noGroups')}</span>
          </Alert>
        )}
        <ListGroup>
          {filteredGroups.map((group, index) => (
            <ListGroup.Item key={group.id} className="d-flex align-items-center">
              <div className="pe-3">
                <FontAwesomeIcon icon={solid('users')} size="lg" fixedWidth />
              </div>

              <Col>
                <h6 className="mb-0">{group.name}</h6>
              </Col>

              <div className="ps-3 text-end">
                <IconButton onClick={manageGroup(group)}>
                  <FontAwesomeIcon className="text-primary" icon={solid('user-cog')} />
                </IconButton>
                {loggedUser!.role === UserRole.ADMIN && <>
                  <IconButton className="ms-2" onClick={editGroup(index)}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </IconButton>
                  <IconButton className="ms-2" onClick={openDeleteModal(group)}>
                    <FontAwesomeIcon className="text-danger" icon={solid('trash')} />
                  </IconButton>
                </>
                }
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </>
    )
  }

  const renderAsyncContent = () => {
    return groups
      ? renderGroups()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Card>
        <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
          <Card.Title className="mb-0">{t('groupsList.title')}</Card.Title>

          {loggedUser!.role === UserRole.ADMIN &&
            <Button variant="success" size="sm" onClick={openCreateModal}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">{t('groupsList.create')}</span>
            </Button>
          }
        </div>

        <Card.Body>{renderAsyncContent()}</Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.deleteTitle', { item: t('common.group') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('groupsList.deleteMessage')} <b>{groupToDelete?.name}</b>?

          <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
          <Button variant="danger" size="sm" onClick={deleteGroup}>{t('common.delete')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCreateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? t('common.edit') : t('common.create')} {t('common.group')}</Modal.Title>
        </Modal.Header>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t('common.name')}</Form.Label>
              <Form.Control placeholder={t('groupsList.groupName')} size="sm" value={newGroup.name} onChange={handleGroupChange} required />
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.name') })}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleCreateModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}